import {
  AspectRatio,
  Box,
  Flex,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import type { AttachmentResponse } from '@markku/types'

import { useState } from 'react'
import { validateFileAge } from '../utils/fileValidation'
import { compressImage } from '../utils/image'
import { AddPhotoButton } from './AddPhotoButton'
import { PhotoPreviewModal } from './PhotoPreviewModal'

// Support both AttachmentResponse and simple photo types
type SimplePhoto = {
  id: number
  downloadUrl?: string | null
}

export interface PhotoGalleryProps {
  photos: AttachmentResponse[] | SimplePhoto[]
  label?: string
  compact?: boolean
  onUpdate?: (id: number, description: string) => Promise<void>
  onDelete?: (id: number) => void | Promise<void>
  // Capture mode props
  onCapture?: (file: File) => void
  disabled?: boolean
  enforceMaxAge?: boolean
  showAddButton?: boolean
}

// Type guard to check if photo is AttachmentResponse
function isAttachmentResponse(
  photo: AttachmentResponse | SimplePhoto,
): photo is AttachmentResponse {
  return 'filename' in photo
}

export function PhotoGallery({
  photos,
  label,
  compact = false,
  onUpdate,
  onDelete,
  onCapture,
  disabled = false,
  enforceMaxAge = true,
  showAddButton = true,
}: PhotoGalleryProps) {
  const [previewOpened, { open: openPreview, close: closePreview }] =
    useDisclosure(false)
  const [previewIndex, setPreviewIndex] = useState(0)

  // Determine if we're in capture mode
  const isCaptureMode = !!onCapture

  // Return null for display mode when no photos
  if (!isCaptureMode && photos.length === 0) {
    return null
  }

  const handleFileSelect = async (file: File) => {
    if (!onCapture) return

    if (!file.type.startsWith('image/')) {
      return
    }

    // Validate photo age if enforcement is enabled
    const maxAgeHours = enforceMaxAge
      ? Number(import.meta.env.VITE_MAX_PHOTO_AGE_HOURS)
      : undefined

    if (!(await validateFileAge(file, maxAgeHours))) {
      return
    }

    // Compress image before upload
    const compressedFile = await compressImage(file)
    onCapture(compressedFile)
  }

  // Preview handlers - unified for both modes
  const handleThumbnailClick = (index: number) => {
    setPreviewIndex(index)
    openPreview()
  }

  // Render capture mode
  if (isCaptureMode) {
    const thumbnailSize = compact
      ? { width: '100%', height: 80 }
      : { width: 100, height: 100 }
    const displayPhotos = compact ? photos.slice(0, 1) : photos
    const hasPhotos = photos.length > 0

    const Container = compact ? Stack : Flex
    const containerProps = compact
      ? { gap: 4 }
      : { gap: 'sm', wrap: 'wrap' as const }

    return (
      <>
        <Stack gap={4}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="sm" fw={500} c="dimmed">
              {label}
            </Text>

            {!disabled && hasPhotos && showAddButton && (
              <AddPhotoButton
                onFileSelect={handleFileSelect}
                variant="subtle"
              />
            )}
          </Group>

          <Container {...containerProps}>
            {displayPhotos.map((photo, index) => (
              <Box
                key={photo.id}
                style={{
                  position: 'relative',
                  ...thumbnailSize,
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <UnstyledButton
                  onClick={() => handleThumbnailClick(index)}
                  disabled={disabled}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                  }}
                >
                  <Image
                    src={photo.downloadUrl}
                    alt={`${label} ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </UnstyledButton>
              </Box>
            ))}

            {!hasPhotos && !disabled && (
              <AddPhotoButton
                onFileSelect={handleFileSelect}
                size="compact-md"
                variant="light"
              />
            )}
          </Container>
        </Stack>

        {/* Preview modal for capture mode */}
        <PhotoPreviewModal
          photos={photos}
          initialIndex={previewIndex}
          opened={previewOpened}
          onClose={closePreview}
          onDelete={onDelete}
          onUpdate={onUpdate}
          disabled={disabled}
          label={label}
        />
      </>
    )
  }

  // Render display mode
  return (
    <>
      <Box>
        {label && (
          <Text size="sm" fw={500} mb="xs">
            {label}
          </Text>
        )}
        <SimpleGrid cols={4} spacing={compact ? 'xs' : 'sm'}>
          {photos.map((photo, index) => (
            <Box key={photo.id}>
              <AspectRatio ratio={1}>
                <Image
                  src={photo.downloadUrl}
                  alt={
                    isAttachmentResponse(photo)
                      ? photo.description || photo.filename
                      : `Photo ${index + 1}`
                  }
                  radius="sm"
                  fit="cover"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleThumbnailClick(index)}
                />
              </AspectRatio>
              {isAttachmentResponse(photo) && photo.description && (
                <Text size="xs" c="dimmed" mt={4} lineClamp={1}>
                  {photo.description}
                </Text>
              )}
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Preview modal for display mode */}
      <PhotoPreviewModal
        photos={photos}
        initialIndex={previewIndex}
        opened={previewOpened}
        onClose={closePreview}
        onDelete={onDelete}
        label={label}
        onUpdate={onUpdate}
        showConfirmation={true}
      />
    </>
  )
}
